import { isAdmin, isCommercial, isProjectManager, isSMT } from '../../interfaces/userRoles';
import { React, Box, Divider, useLocation, Drawer, List, ListItemIcon, ListItemText, useNavigate, Link, ListItemButton, rsk_logo, logoBg, Tooltip, Avatar } from '../../utils/globalImports';
import { DashboardIcon, PersonIcon, Groups2Icon, ExitToAppIcon, Inventory2Icon, AccountTreeIcon, GridOnOutlinedIcon } from '../../utils/globalImports';
import { CustomConfirmDialog, useApi } from '../../utils/globalImports';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';

const drawerWidth = 240;

interface SidebarProps {
    open: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open }) => {
    const { execute } = useApi();
    const navigate = useNavigate();
    // const { logout } = useAuth();
    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState('Dashboard');

    const handleListItemClick = (item: string) => {
        if (item === 'Logout') {
            setDialogOpen(true);
        }
    };

    const location = useLocation();
    React.useEffect(() => {
        switch (location.pathname) {
            case '/dashboard':
                setSelectedItem('Dashboard');
                break;
            case (location.pathname.startsWith("/framework/") ? location.pathname : ""):
                setSelectedItem('Frameworks');
                break;
            case '/user/users':
                setSelectedItem('Users');
                break;
            case (location.pathname.startsWith("/client/") ? location.pathname : ""):
                setSelectedItem('Clients');
                break;
            case (location.pathname.startsWith("/lookup/") ? location.pathname : ""):
                setSelectedItem('Lookup');
                break;
            case (location.pathname.startsWith("/project/") ? location.pathname : ""):
            case (location.pathname.startsWith("/proposal/") ? location.pathname : ""):
                setSelectedItem('Projects');
                break;
            case (location.pathname.startsWith("/profile") ? location.pathname : ""):
                setSelectedItem('Profile');
                break;
            case (location.pathname.startsWith("/ifs-tracker") ? location.pathname : ""):
                setSelectedItem('IFSTracker');
                break;
             case(location.pathname.startsWith("/invoicing")? location.pathname:""):
                setSelectedItem('Invoicingform');
                break; 
            case(location.pathname.startsWith("/invoiceList")? location.pathname:""):
                setSelectedItem('invoiceList');
                break;
            case (location.pathname.startsWith("/settings") ? location.pathname : ""):
                setSelectedItem('Settings');
                break;
            default:
                setSelectedItem('');
        }
    }, [location.pathname]);

    const handleConfirmLogout = async () => {
        setDialogOpen(false);
        localStorage.removeItem('userId');
        localStorage.removeItem("name");
        localStorage.removeItem("role");
        // Call logout API
        await execute('post', '/Auth/logout');
        navigate('/login');
    };

    const handleCancelLogout = () => {
        setDialogOpen(false);
    };

    const listItemStyles = {
        position: 'relative',
        '&.Mui-selected': {
            backgroundColor: 'rgba(169, 201, 81, 0.14) !important',
            color: '#96c121',
        },
        '&.Mui-selected::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: '3%',
            backgroundColor: '#96c121', // Green left border (rectangle)
        },
        '&.Mui-selected::after': {
            content: '""',
            position: 'absolute',
            right: 0, // Triangle on the right
            top: '50%',
            transform: 'translateY(-50%)', // Center vertically
            width: '0',
            height: '0',
            borderStyle: 'solid',
            borderWidth: '20px 20px 20px 0', // Triangle pointing right
            borderColor: 'transparent #96c121 transparent transparent', // Triangle color
        },
    };

    return (
        <>
            <Drawer
                sx={{                
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Logo and Close button in a single row */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center', // Centers horizontally
                            alignItems: 'center', // Centers vertically
                            height: '22vh', // Ensures the Box takes up full height of the container
                            backgroundImage: `url(${logoBg})`, // Set the background image
                            backgroundSize: 'cover', // Makes sure the background image covers the entire box
                            backgroundPosition: 'center', // Centers the background image
                            position: 'relative', // Required to stack the elements
                        }}
                    >

                        {/* Logo */}
                        <Box
                            component="img"
                            src={rsk_logo}
                            alt="RSK Logo"
                            sx={{
                                width: '180px',
                                height: 'auto',
                            }}
                        />
                    </Box>

                    <Divider />

                    {/* Sidebar List */}
                    <List component="nav" sx={{ flexGrow: 1, mt: 3 }}>
                        <ListItemButton
                            selected={selectedItem === 'Dashboard'}
                            onClick={() => handleListItemClick('Dashboard')}
                            component={Link}
                            to="/dashboard"
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Dashboard' ? '#96c121' : '#1e66ab' }}>
                                <DashboardIcon />
                            </ListItemIcon>
                            <ListItemText primary="Dashboard" sx={{ color: selectedItem === 'Dashboard' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        <ListItemButton

                            selected={selectedItem === 'Frameworks'}
                            onClick={() => handleListItemClick('Frameworks')}
                            component={Link}
                            to="/framework/frameworks"
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Frameworks' ? '#96c121' : '#1e66ab' }}>
                                <AccountTreeIcon />
                            </ListItemIcon>
                            <ListItemText primary="Frameworks" sx={{ color: selectedItem === 'Frameworks' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        <ListItemButton
                            selected={selectedItem === 'Projects'}
                            onClick={() => handleListItemClick('Projects')}
                            sx={listItemStyles}
                            component={Link}
                            to="/project/projects"
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Projects' ? '#96c121' : '#1e66ab' }}>
                                <Inventory2Icon />
                            </ListItemIcon>
                            <ListItemText primary="Projects" sx={{ color: selectedItem === 'Projects' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to="/ifs-tracker"
                            selected={selectedItem === 'IFSTracker'}
                            onClick={() => handleListItemClick('IFSTracker')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'IFSTracker' ? '#96c121' : '#1e66ab' }}>
                                <AssessmentIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Income Forecast"
                                sx={{ color: selectedItem === 'IFSTracker' ? '#96c121' : '#1e66ab' }}
                            />
                        </ListItemButton>

                        {(isCommercial() || isSMT() || isAdmin()) && (
                        <ListItemButton
                            component={Link}
                            to="/Baseline/PreviousBaselineList"
                            selected={selectedItem === 'Baseline List'}
                            onClick={() => handleListItemClick('Baseline List')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Baseline List' ? '#96c121' : '#1e66ab' }}>
                                <Groups2Icon />
                            </ListItemIcon>
                            <ListItemText primary="Baseline List" sx={{ color: selectedItem === 'Baseline List' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        )}
                        {(isProjectManager()) && (
                        <ListItemButton
                            component={Link}
                            to="/invoicing"
                            selected={selectedItem === 'Invoicingform'}
                            onClick={() => handleListItemClick('Invoicingform')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Invoicingform' ? '#96c121' : '#1e66ab' }}>
                                <AssignmentIcon />
                                
                            </ListItemIcon>
                            <ListItemText primary="Invoicing Form" sx={{ color: selectedItem === 'Invoicingform' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        )}
                        {(isSMT()) && (
                        <ListItemButton
                            component={Link}
                            to="/invoiceList"
                            selected={selectedItem === 'invoiceList'}
                            onClick={() => handleListItemClick('invoiceList')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'invoiceList' ? '#96c121' : '#1e66ab' }}>
                                <Groups2Icon />
                            </ListItemIcon>
                            <ListItemText primary="Invoices" sx={{ color: selectedItem === 'invoiceList' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        )}
                        
                        
                        <ListItemButton
                            component={Link}
                            to="/client/clients"
                            selected={selectedItem === 'Clients'}
                            onClick={() => handleListItemClick('Clients')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Clients' ? '#96c121' : '#1e66ab' }}>
                                <Groups2Icon />
                            </ListItemIcon>
                            <ListItemText primary="Clients" sx={{ color: selectedItem === 'Clients' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to="/lookup/client-category"
                            selected={selectedItem === 'Lookup'}
                            onClick={() => handleListItemClick('Lookup')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Lookup' ? '#96c121' : '#1e66ab' }}>
                                <GridOnOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Look Up Table" sx={{ color: selectedItem === 'Lookup' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        {(isCommercial() || isSMT() || isAdmin()) &&(
                        <ListItemButton
                            component={Link}
                            to="/settings"
                            selected={selectedItem === 'Settings'}
                            onClick={() => handleListItemClick('Settings')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Settings' ? '#96c121' : '#1e66ab' }}>
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary="Settings" sx={{ color: selectedItem === 'Settings' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        )}
                        <ListItemButton
                            component={Link}
                            to="/user/users"
                            selected={selectedItem === 'Users'}
                            onClick={() => handleListItemClick('Users')}
                            sx={listItemStyles}
                        >
                            <ListItemIcon sx={{ color: selectedItem === 'Users' ? '#96c121' : '#1e66ab' }}>
                                <PersonIcon />
                            </ListItemIcon>
                            <ListItemText primary="Users" sx={{ color: selectedItem === 'Users' ? '#96c121' : '#1e66ab' }} />
                        </ListItemButton>
                        

                    </List>

                    {/* Logout Item */}
                    <List sx={{ display: 'flex', flexDirection: 'row', marginTop: 'auto' }}>
                        <ListItemButton onClick={() => handleListItemClick('Logout')}>
                            <ListItemIcon sx={{ color: '#96c121' }}>
                                <ExitToAppIcon />
                            </ListItemIcon>
                            <ListItemText primary="Logout" sx={{ color: '#96c121' }} />
                        </ListItemButton>
                        <ListItemButton
                            onClick={() => handleListItemClick('Profile')}
                            selected={selectedItem === 'Profile'}
                            component={Link}
                            to="/profile"
                            sx={{
                                backgroundColor: 'transparent', // Default background
                                '&.Mui-selected': {
                                    backgroundColor: 'transparent !important', // Override selected background
                                },
                                '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Optional hover effect
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <Tooltip title="Profile">
                                <ListItemIcon sx={{ justifyContent: 'center', minWidth: '100%' }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: selectedItem === 'Profile' ? '#1976d2' : '#96c121',
                                            color: '#fff',
                                            width: 32,
                                            height: 32,
                                            fontSize: 14,
                                        }}
                                    >
                                        {(() => {
                                            const firstName = localStorage.getItem('name')?.split(' ')[0] || '';
                                            const lastName = localStorage.getItem('name')?.split(' ')[1] || '';
                                            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                                        })()}
                                    </Avatar>
                                </ListItemIcon>
                            </Tooltip>
                        </ListItemButton>

                    </List>
                </Box>
            </Drawer>

            {/* Confirmation Dialog */}
            <CustomConfirmDialog
                open={isDialogOpen}
                title="Confirm Logout"
                message="Are you sure you want to logout?"
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
                onClose={handleCancelLogout}
            />
        </>
    );
};

export default Sidebar;